<script setup>
import {useBaseStore} from "~/stores/base";
// 引入swiper组件
import {Swiper, SwiperSlide} from "swiper/vue";
import {Navigation, Pagination, Autoplay} from 'swiper/modules';
import {PATH_PRODUCT} from "~/utils/constants";
import _ from "lodash"
import {wishlistAdd} from "~/api/api.wishlist";
import {toLogin} from "~/utils/common";
import {ELEMENT_TYPE, labelFormatter} from "~/utils/smartlook";
import ModalWishlistCapsule from "~/components/wishlist/ModalWishlistCapsule.vue";
import {googleTrackLikeProduct} from "~/utils/google-track";

const BNTOMessage = useNuxtApp().$BNTOMessage
const debounce = _.debounce((params, func) => {
  func(params);
}, 300);
const router = useRouter()
const route = useRoute()

/** props **/
const props = defineProps({
  title: {
    default: "",
    required: true
  },
  list: {
    default: () => [],
    required: true
  },
  btnViewAllVisible: {
    default: false
  },
  btnViewAllLink: {
    default: ""
  }
})

/** data **/
const currentHover = ref(-1)

const likeEditingProduct = ref({})
const isShowModalWishlistCapsule = ref(false)

/** computed **/
const isMobile = computed(() => useBaseStore().getterIsMobile)
const isLogin = computed(() => useBaseStore().getterIsLogin)

/** methods **/
const onSwiper = (swiper) => {
};
const onSlideChange = () => {
};
const handleMouseenter = (index) => {
  currentHover.value = index
}
const handleMouseleave = () => {
  currentHover.value = -1
}
// 图片加载完成时的回调
const handleLoad = (item) => {
  item.isLoaded = true
}
// 图片加载失败时的回调
const handleError = () => {
  console.error("图片加载失败")
}

const handleLike = async (item) => {
  if (!isLogin.value) return toLogin({query: {redirect_uri: route.fullPath}})
  const shopProductId = item.shopProductId
  likeEditingProduct.value = {
    ...item,
    productId: shopProductId,
    cover: item.imageSets[0]
  }
  if (!item.isFollow) {
    await wishlistAdd({shopProductId})
    googleTrackLikeProduct(item)
    item.isFollow = true
    BNTOMessage.success({
      title: "saved to all",
      btnText: "add to capsule",
      onClick: () => {
        isShowModalWishlistCapsule.value = true
      }
    })
  } else {
    isShowModalWishlistCapsule.value = true
  }
}
const handleModalWishlistCapsuleClose = (liked) => {
  props.list = props.list.map((item) => {
    if (item.shopProductId === likeEditingProduct.value.productId) item.isFollow = liked
    return item
  })
  isShowModalWishlistCapsule.value = false
}

</script>

<template>
  <div class="module-carousel-count-6-mobile" v-if="isMobile">
    <div class="title">{{ props.title }}</div>
    <div class="carousel">
      <div
        class="item" v-for="(item, index) in list"
        :key="index"
        element-expose-observe-async
        :element-type="ELEMENT_TYPE.PRODUCT_CARD"
        :element-title="`${item.brand} - ${item.title}`"
        :element-label="labelFormatter(title)"
        :element-params="`${PATH_PRODUCT}/${item.shopProductId}/${item.handle}`"
        :element-index="index"
      >
        <nuxt-link
          :to="`${PATH_PRODUCT}/${item.shopProductId}/${item.handle}`"
          class="item-top"
          element-click-observe-async
          :element-type="ELEMENT_TYPE.PRODUCT_CARD"
          :element-title="`${item.brand} - ${item.title}`"
          :element-label="labelFormatter(title)"
          :element-params="`${PATH_PRODUCT}/${item.shopProductId}/${item.handle}`"
          :element-index="index"
        >
          <!-- 占位符 -->
          <div v-if="!item.isLoaded" class="cover-image-skeleton"/>
          <!-- 单图 -->
          <img
            class="cover-img"
            :class="{'cover-img-loaded': item.isLoaded}"
            :src="item.imageSets.find(cover => cover.type === 'main')?.url"
            alt="cover"
            loading="lazy"
            @load="handleLoad(item)"
            @error="handleError(item)"
          >
          <!-- 喜欢 -->
          <div
            class="like"
            @click.stop.prevent="handleLike(item)"
            element-click-observe-async
            :element-type="ELEMENT_TYPE.BUTTON"
            :element-title="`${item.brand} - ${item.title}`"
            :element-label="item.isFollow ? ELEMENT_LABEL.LIKE : ELEMENT_LABEL.UNLIKE"
            :element-params="`productId=${item.shopProductId}`"
            :element-index="index"
          >
            <img v-show="item.isFollow" src="@/assets/img/icon-liked.svg" alt="icon">
            <img v-show="!item.isFollow" src="@/assets/img/icon-unlike.svg" alt="icon">
          </div>
        </nuxt-link>
        <nuxt-link
          :to="`${PATH_PRODUCT}/${item.shopProductId}/${item.handle}`"
          class="item-bottom"
          element-click-observe-async
          :element-type="ELEMENT_TYPE.PRODUCT_CARD"
          :element-title="`${item.brand} - ${item.title}`"
          :element-label="labelFormatter(title)"
          :element-params="`${PATH_PRODUCT}/${item.shopProductId}/${item.handle}`"
          :element-index="index"
        >
          <div class="brand">{{ item.brand }}</div>
          <div class="name">{{ item.title }}</div>
          <div class="price">Retail: ${{ item.price }} <img v-if="item.premium" class="premium"
                                                            src="@/assets/img/icon-diamond.svg"
                                                            alt="diamond"></div>
        </nuxt-link>
      </div>
    </div>
    <nuxt-link :to="btnViewAllLink" class="btn-secondary" v-if="btnViewAllVisible">
      view all
    </nuxt-link>
  </div>
  <div class="module-carousel-count-6-desktop" v-else>
    <div class="title">{{ props.title }}</div>
    <div class="carousel">
      <swiper
        :loop="list.length > 6"
        grab-cursor
        :autoplay="false"
        :navigation="{
          prevEl: '.navigation-left',
          nextEl: '.navigation-right'
        }"
        :modules="[Navigation, Pagination, Autoplay]"
        :slides-per-view="6"
        :slides-per-group="6"
        :space-between="10"
        @swiper="onSwiper"
        @slideChange="onSlideChange"
      >
        <swiper-slide
          v-for="(item, index) in list"
          :key="index"
          element-expose-observe-async
          :element-type="ELEMENT_TYPE.PRODUCT_CARD"
          :element-title="`${item.brand} - ${item.title}`"
          :element-label="labelFormatter(title)"
          :element-params="`${PATH_PRODUCT}/${item.shopProductId}/${item.handle}`"
          :element-index="index"
        >
          <nuxt-link
            :to="`${PATH_PRODUCT}/${item.shopProductId}/${item.handle}`"
            class="slide-top"
            @mouseenter="handleMouseenter(index)"
            @mouseleave="handleMouseleave"
            element-click-observe-async
            :element-type="ELEMENT_TYPE.PRODUCT_CARD"
            :element-title="`${item.brand} - ${item.title}`"
            :element-label="labelFormatter(title)"
            :element-params="`${PATH_PRODUCT}/${item.shopProductId}/${item.handle}`"
            :element-index="index"
          >
            <!-- 占位符 -->
            <div v-if="!item.isLoaded" class="cover-image-skeleton"/>

            <!-- 图+视频 -->
            <template v-if="item.video.url">
              <img
                v-show="currentHover !== index"
                class="cover-img"
                :class="{'cover-img-loaded': item.isLoaded}"
                :src="item.imageSets.find(cover => cover.type === 'main')?.url"
                alt="cover"
                loading="lazy"
                @load="handleLoad(item)"
                @error="handleError(item)"
              >
              <video
                v-show="currentHover === index" class="cover-video"
                :poster="item.imageSets.find(cover => cover.type === 'main')?.url"
                autoplay
                muted
                loop
              >
                <source :src="item.video.url" type="video/mp4">
                Your browser does not support the video tag.
              </video>
            </template>
            <!-- 双图 -->
            <template v-else-if="item.imageSets.length === 2">
              <img
                v-show="currentHover !== index"
                class="cover-img"
                :class="{'cover-img-loaded': item.isLoaded}"
                :src="item.imageSets.find(cover => cover.type === 'main')?.url"
                alt="cover"
                loading="lazy"
                @load="handleLoad(item)"
                @error="handleError(item)"
              >
              <img
                v-show="currentHover === index"
                class="cover-img cover-img-loaded"
                :src="item.imageSets.find(cover => cover.type === 'hover')?.url"
                alt="cover"
              >
            </template>
            <!-- 单图 -->
            <img
              v-else
              class="cover-img"
              :class="{'cover-img-loaded': item.isLoaded}"
              :src="item.imageSets.find(cover => cover.type === 'main')?.url"
              alt="cover"
              loading="lazy"
              @load="handleLoad(item)"
              @error="handleError(item)"
            >

            <!-- 喜欢 -->
            <div
              class="like"
              @click.stop.prevent="handleLike(item)"
              element-click-observe-async
              :element-type="ELEMENT_TYPE.BUTTON"
              :element-title="`${item.brand} - ${item.title}`"
              :element-label="item.isFollow ? ELEMENT_LABEL.LIKE : ELEMENT_LABEL.UNLIKE"
              :element-params="`productId=${item.shopProductId}`"
              :element-index="index"
            >
              <img v-show="item.isFollow" src="@/assets/img/icon-liked.svg" alt="icon">
              <img v-show="!item.isFollow" src="@/assets/img/icon-unlike.svg" alt="icon">
            </div>
          </nuxt-link>
          <nuxt-link
            :to="`${PATH_PRODUCT}/${item.shopProductId}/${item.handle}`"
            class="slide-bottom"
            element-click-observe-async
            :element-type="ELEMENT_TYPE.PRODUCT_CARD"
            :element-title="`${item.brand} - ${item.title}`"
            :element-label="labelFormatter(title)"
            :element-params="`${PATH_PRODUCT}/${item.shopProductId}/${item.handle}`"
            :element-index="index"
          >
            <div class="brand">{{ item.brand }}</div>
            <div class="name">{{ item.title }}</div>
            <div class="price">Retail: ${{ item.price }} <img v-if="item.premium" class="premium"
                                                              src="@/assets/img/icon-diamond.svg"
                                                              alt="diamond"></div>
          </nuxt-link>
        </swiper-slide>

        <!-- If we need navigation buttons -->
        <div class="navigation-left" v-if="list.length > 6">
          <img src="@/assets/img/icon-left-small.svg" alt="left">
        </div>
        <div class="navigation-right" v-if="list.length > 6">
          <img src="@/assets/img/icon-right-small.svg" alt="right">
        </div>
      </swiper>
    </div>
    <div class="btn-container" v-if="btnViewAllVisible">
      <nuxt-link :to="btnViewAllLink" class="btn-secondary">
        view all
      </nuxt-link>
    </div>
  </div>
  <modal-wishlist-capsule
    :open="isShowModalWishlistCapsule"
    :product="likeEditingProduct"
    @close="handleModalWishlistCapsuleClose"
  />
</template>

<style scoped lang="scss">
@import "src/assets/config";

.module-carousel-count-6-mobile {
  padding: 10px 16px 10px 16px;
  margin-top: 16px;

  .title {
    /* Headline/small */
    font-family: "Alternate Gothic No3 D";
    font-size: 24px;
    font-style: normal;
    font-weight: 400;
    line-height: 32px; /* 133.333% */
    text-transform: uppercase;
    text-align: center;
  }

  .carousel {
    display: flex;
    overflow-x: scroll;
    overflow-y: hidden;
    margin-top: 10px;

    .item {
      flex-shrink: 0;
      margin-right: 10px;
      cursor: pointer;
      width: 37.3vw;
      height: 69.9vw;

      .item-top {
        width: 100%;
        display: flex;
        justify-content: flex-end;
        position: relative;

        .cover-image-skeleton {
          flex-shrink: 0;

          position: absolute;
          left: 0;
          top: 0;

          width: 37.3vw;
          height: 52.3vw;
          background: linear-gradient(90deg, var(--loader-background-color) 25%, var(--loader-highlight-color) 50%, var(--loader-background-color) 75%);
          background-size: 200% 100%;
          animation: loading 2s infinite ease-in-out;
        }

        @keyframes loading {
          0% {
            background-position: 200% 0;
          }
          100% {
            background-position: -200% 0;
          }
        }

        .cover-img {
          flex-shrink: 0;
          width: 37.3vw;
          aspect-ratio: 1 / 1.4;
          object-fit: cover;
          display: block;
          transition: opacity 0.3s ease-in-out;
          opacity: 0; /* 初始隐藏 */
        }

        /* 图片加载完成后显示 */
        .cover-img-loaded {
          opacity: 1;
        }

        .like {
          cursor: pointer;
          display: flex;
          justify-content: center;
          align-items: center;
          width: 32px;
          height: 32px;
          position: absolute;
          right: 2px;
          top: 2px;

          img {
            pointer-events: none; // 使其不被点击，直接穿透到父级去
            width: 24px;
            height: 24px;
          }
        }
      }

      .item-bottom {
        width: 100%;
        height: 74px;
        padding: 8px 8px 12px 8px;

        div {
          pointer-events: none; // 使其不被点击，直接穿透到父级去
        }

        .brand {
          /* Text xs/Bold */
          font-family: "TWK Lausanne";
          font-size: 12px;
          font-style: normal;
          font-weight: 650;
          line-height: 18px; /* 150% */
        }

        .name {
          /* Text xs/Regular */
          font-family: "TWK Lausanne";
          font-size: 12px;
          font-style: normal;
          font-weight: 300;
          line-height: 18px; /* 150% */

          // 单行
          word-break: break-all;
          word-wrap: anywhere;
          display: -webkit-box;
          overflow: hidden;
          text-overflow: ellipsis;
          -webkit-box-orient: vertical;
          line-clamp: 1;
          -webkit-line-clamp: 1;
        }

        .price {
          /* Text xs/Regular */
          font-family: "TWK Lausanne";
          font-size: 12px;
          font-style: normal;
          font-weight: 300;
          line-height: 18px; /* 150% */

          display: flex;
          align-items: center;
          gap: 2px;

          .premium {
            width: 18px;
            height: 18px;
            flex-shrink: 0;
          }
        }
      }

      &:last-child {
        margin-right: 0px;
      }
    }
  }

  .btn-secondary {
    width: 144px;
    margin: 10px auto;
  }
}

.module-carousel-count-6-desktop {
  margin-top: 16px;
  padding-top: 10px;
  padding-bottom: 10px;

  .title {
    padding-top: 18px;
    width: 100%;
    min-width: $content-1280-width;
    text-align: center;

    font-family: "Alternate Gothic No3 D";
    font-size: 24px;
    font-style: normal;
    font-weight: 400;
    line-height: 32px; /* 133.333% */
    text-transform: uppercase;
  }

  .carousel {
    margin-top: 10px;

    .swiper {
      width: $content-1280-width;
      position: relative;

      .swiper-slide {
        width: fit-content;
        height: fit-content;
        display: flex;
        flex-direction: column;

        .slide-top {
          width: fit-content;
          height: fit-content;
          display: flex;
          justify-content: flex-end;
          position: relative;

          cursor: pointer;

          .cover-image-skeleton {
            flex-shrink: 0;

            position: absolute;
            left: 0;
            top: 0;

            width: 205px;
            height: 287px;
            background: linear-gradient(90deg, var(--loader-background-color) 25%, var(--loader-highlight-color) 50%, var(--loader-background-color) 75%);
            background-size: 200% 100%;
            animation: loading 2s infinite ease-in-out;
          }

          @keyframes loading {
            0% {
              background-position: 200% 0;
            }
            100% {
              background-position: -200% 0;
            }
          }

          .cover-img {
            flex-shrink: 0;
            pointer-events: none; // 使其不被点击，直接穿透到父级去
            width: 205px;
            aspect-ratio: 1 / 1.4;
            object-fit: cover; /* 确保图片按比例填充 */
            display: block;
            transition: opacity 0.3s ease-in-out;
            opacity: 0; /* 初始隐藏 */
          }

          /* 图片加载完成后显示 */
          .cover-img-loaded {
            opacity: 1;
          }

          .cover-video {
            flex-shrink: 0;
            pointer-events: none; // 使其不被点击，直接穿透到父级去
            width: 205px;
            height: 287px;
            object-fit: cover;
            object-position: center center;
          }

          .like {
            cursor: pointer;
            display: flex;
            justify-content: center;
            align-items: center;
            position: absolute;
            right: 2px;
            top: 2px;

            width: 42px;
            height: 42px;

            img {
              pointer-events: none; // 使其不被点击，直接穿透到父级去
              width: 32px;
              height: 32px;
            }
          }
        }

        .slide-bottom {
          width: 100%;
          height: 88px;
          padding: 8px;
          cursor: pointer;

          div {
            pointer-events: none; // 使其不被点击，直接穿透到父级去
          }

          &:hover {
            text-decoration: underline;
          }

          .brand {
            width: fit-content;
            text-overflow: ellipsis;
            font-family: "TWK Lausanne";
            font-size: 16px;
            font-style: normal;
            font-weight: 650;
            line-height: 24px; /* 150% */

            // 单行
            word-break: break-all;
            word-wrap: anywhere;
            display: -webkit-box;
            overflow: hidden;
            text-overflow: ellipsis;
            -webkit-box-orient: vertical;
            line-clamp: 1;
            -webkit-line-clamp: 1;
          }

          .name {
            width: fit-content;
            text-overflow: ellipsis;
            font-family: "TWK Lausanne";
            font-size: 16px;
            font-style: normal;
            font-weight: 300;
            line-height: 24px; /* 150% */

            // 单行
            word-break: break-all;
            word-wrap: anywhere;
            display: -webkit-box;
            overflow: hidden;
            text-overflow: ellipsis;
            -webkit-box-orient: vertical;
            line-clamp: 1;
            -webkit-line-clamp: 1;
          }

          .price {
            width: fit-content;
            text-align: left;
            text-overflow: ellipsis;
            font-family: "TWK Lausanne";
            font-size: 16px;
            font-style: normal;
            font-weight: 300;
            line-height: 24px; /* 150% */

            display: flex;
            align-items: center;
            gap: 2px;

            .premium {
              width: 18px;
              height: 18px;
              flex-shrink: 0;
            }
          }

        }
      }

      .navigation-left {
        position: absolute;
        display: flex;
        left: 0;
        top: 50%;
        z-index: 1;
        transform: translate(0, -150%);
        cursor: pointer;

        img {
          width: 40px;
          height: 40px;
        }
      }

      .navigation-right {
        position: absolute;
        display: flex;
        right: 0;
        top: 50%;
        z-index: 1;
        transform: translate(0, -150%);
        cursor: pointer;

        img {
          width: 40px;
          height: 40px;
        }
      }
    }
  }

  .btn-container {
    width: 100%;
    min-width: $content-1280-width;
    margin-top: 10px;

    .btn-secondary {
      width: 144px;
      margin: 0 auto;
    }
  }
}
</style>
